import React, { useEffect, useState, useRef, useCallback } from "react";
import { Play, Pause, X, ChevronLeft, ChevronRight, Volume2, VolumeX } from "lucide-react";
import { resolveFileUrl } from "../lib/urls";

/**
 * ReelPlayer — full-screen auto-advancing slideshow with Ken Burns pan on
 * photos and native playback on videos. Escape / X button closes.
 *
 *   - Photos: 4-second display with a subtle scale animation (Ken Burns).
 *   - Videos: play natively; auto-advance when the `ended` event fires.
 *   - Left/Right arrow keys and swipe gestures navigate.
 *   - Space toggles play/pause.
 *   - Persistent audio-mute preference in localStorage so videos don't
 *     always autoplay muted on repeat viewings.
 */
const PHOTO_DURATION_MS = 4000;

const ReelPlayer = ({ uploads, title, subtitle, onClose }) => {
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [muted, setMuted] = useState(() => {
    try { return localStorage.getItem("gp_reel_muted") !== "0"; } catch { return true; }
  });
  const videoRef = useRef(null);
  const timerRef = useRef(null);

  const current = uploads[index];
  const total = uploads.length;

  const next = useCallback(() => setIndex((i) => (i + 1) % total), [total]);
  const prev = useCallback(() => setIndex((i) => (i - 1 + total) % total), [total]);

  useEffect(() => {
    try { localStorage.setItem("gp_reel_muted", muted ? "1" : "0"); } catch { /* no-op */ }
  }, [muted]);

  useEffect(() => {
    if (!playing) return;
    if (current?.kind === "video") return; // video advances on its own `ended` event
    timerRef.current = setTimeout(next, PHOTO_DURATION_MS);
    return () => clearTimeout(timerRef.current);
  }, [index, playing, current, next]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === " ") { e.preventDefault(); setPlaying((p) => !p); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [next, prev, onClose]);

  // Swipe navigation for touch devices
  const touchStart = useRef(null);
  const onTouchStart = (e) => { touchStart.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStart.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStart.current;
    if (dx > 50) prev();
    else if (dx < -50) next();
    touchStart.current = null;
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black flex items-center justify-center"
      data-testid="reel-player"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {/* Media */}
      {current?.kind === "video" ? (
        <video
          ref={videoRef}
          key={current.file_url}
          src={resolveFileUrl(current.file_url)}
          className="max-h-full max-w-full object-contain"
          autoPlay={playing}
          muted={muted}
          playsInline
          onEnded={next}
          data-testid="reel-player-video"
        />
      ) : (
        <img
          key={current?.file_url}
          src={resolveFileUrl(current?.file_url)}
          alt={current?.uploader_name || ""}
          className="max-h-full max-w-full object-contain reel-kenburns"
          data-testid="reel-player-photo"
        />
      )}

      {/* Header — title + close */}
      <div className="absolute top-0 left-0 right-0 p-4 md:p-6 flex items-start justify-between bg-gradient-to-b from-black/60 to-transparent pointer-events-none">
        <div className="text-white pointer-events-none">
          <div className="font-serif italic text-xl md:text-3xl">{title}</div>
          {subtitle && <div className="text-white/70 text-sm mt-0.5">{subtitle}</div>}
        </div>
        <button
          onClick={onClose}
          data-testid="reel-player-close"
          className="pointer-events-auto w-10 h-10 rounded-full bg-white/10 hover:bg-white/25 text-white flex items-center justify-center"
          aria-label="Close reel"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Bottom control bar */}
      <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6 flex items-center justify-center gap-3 bg-gradient-to-t from-black/60 to-transparent">
        <button
          onClick={prev}
          data-testid="reel-player-prev"
          className="w-11 h-11 rounded-full bg-white/10 hover:bg-white/25 text-white flex items-center justify-center"
          aria-label="Previous"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <button
          onClick={() => setPlaying((p) => !p)}
          data-testid="reel-player-toggle"
          className="w-14 h-14 rounded-full bg-coral-300 hover:bg-coral-400 text-white flex items-center justify-center"
          aria-label={playing ? "Pause" : "Play"}
        >
          {playing ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6 ml-0.5" />}
        </button>
        <button
          onClick={next}
          data-testid="reel-player-next"
          className="w-11 h-11 rounded-full bg-white/10 hover:bg-white/25 text-white flex items-center justify-center"
          aria-label="Next"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
        <button
          onClick={() => setMuted((m) => !m)}
          data-testid="reel-player-mute"
          className="w-11 h-11 rounded-full bg-white/10 hover:bg-white/25 text-white flex items-center justify-center ml-2"
          aria-label={muted ? "Unmute" : "Mute"}
        >
          {muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
        </button>
      </div>

      {/* Progress dots */}
      <div className="absolute bottom-24 md:bottom-28 left-0 right-0 flex justify-center gap-1.5">
        {uploads.map((_, i) => (
          <span
            key={i}
            data-testid={`reel-dot-${i}`}
            className={`h-1 rounded-full transition-all ${i === index ? "bg-white w-6" : "bg-white/40 w-1.5"}`}
          />
        ))}
      </div>

      <style>{`
        @keyframes reelKenBurns {
          0% { transform: scale(1.05) translate(0, 0); }
          100% { transform: scale(1.15) translate(-1%, -1%); }
        }
        .reel-kenburns { animation: reelKenBurns ${PHOTO_DURATION_MS}ms linear both; }
      `}</style>
    </div>
  );
};

export default ReelPlayer;
